import { setLoading } from "src/store/customizer/CustomizerSlice";
import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";

const hasUploadFile = (v) =>
  v instanceof File ||
  v instanceof FileList ||
  (Array.isArray(v) && v.some((x) => x instanceof File)) ||
  (v && typeof v === "object" && v.file instanceof File) ||
  (v && typeof v === "object" && v.originFileObj instanceof File);

const extractUploadFiles = (fileValue) => {
  if (!fileValue) return [];
  if (fileValue instanceof File) return [fileValue];
  if (fileValue instanceof FileList) return Array.from(fileValue).filter((f) => f instanceof File);
  if (Array.isArray(fileValue)) {
    return fileValue
      .map((item) => {
        if (item instanceof File) return item;
        if (item?.file instanceof File) return item.file;
        if (item?.originFileObj instanceof File) return item.originFileObj;
        return null;
      })
      .filter(Boolean);
  }
  if (fileValue?.file instanceof File) return [fileValue.file];
  if (fileValue?.originFileObj instanceof File) return [fileValue.originFileObj];
  return [];
};

const appendFilePathField = (formData, fileValue) => {
  if (!fileValue) return;

  if (fileValue instanceof File) {
    formData.append("file_path", fileValue);
    return;
  }

  if (fileValue instanceof FileList || Array.isArray(fileValue)) {
    const files = extractUploadFiles(fileValue);
    const firstFile = files[0];
    if (firstFile) formData.append("file_path", firstFile);
    return;
  }

  if (fileValue && typeof fileValue === "object") {
    const files = extractUploadFiles(fileValue);
    const firstFile = files[0];
    if (firstFile) {
      formData.append("file_path", firstFile);
      return;
    }
  }

  if (typeof fileValue === "string" && fileValue.trim() !== "") {
    formData.append("file_path_url", fileValue.trim());
  }
};

const appendMultipleFilePathFields = (formData, fileValue) => {
  if (!fileValue) return 0;
  const files = extractUploadFiles(fileValue);
  files.forEach((file) => formData.append("file_path", file));
  return files.length;
};

export const GetGalleryItems =
  ({ callback, gallery_id }) =>
  async (dispatch, getState) => {
    try {
      // Backend requires gallery_id as a number and it's required
      // Convert to number if it's a string (from URL params)
      const galleryIdNumber = gallery_id ? Number(gallery_id) : null;
      
      if (!galleryIdNumber || isNaN(galleryIdNumber)) {
        callback(false);
        return;
      }
      
      const response = await PostFunction({
        data: { gallery_id: galleryIdNumber },
        Route: UrlApi.gallery_items.get,
        showalert: false,
        state: getState,
        isMultipart: false, // Backend expects JSON object, not FormData
        dispatch,
      });
      callback(response);
    } catch (err) {
      callback(false);
    }
  };

export const ManageGalleryItems =
  ({ callback, values, editMode }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const bodyFormData = new FormData();
      
      if (editMode && values?.id) {
        bodyFormData.append("id", values.id);
      }
      
      if (values.gallery_id) bodyFormData.append("gallery_id", values.gallery_id);
      if (values.type) bodyFormData.append("type", values.type);
      if (values.title) bodyFormData.append("title", values.title.trim());
      if (values.description) bodyFormData.append("description", values.description.trim());
      if (values.display_order !== undefined) bodyFormData.append("display_order", values.display_order);
      
      const uploadedFilesCount = editMode
        ? 0
        : appendMultipleFilePathFields(bodyFormData, values.file);

      if (
        editMode &&
        values.file_path &&
        !hasUploadFile(values.file) &&
        !(typeof values.file === "string" && values.file.trim() !== "")
      ) {
        appendFilePathField(bodyFormData, values.file_path);
      }

      const route = editMode
        ? UrlApi.gallery_items.edit
        : uploadedFilesCount > 1
          ? UrlApi.gallery_items.addMultiple
          : UrlApi.gallery_items.add;
      
      const response = await PostFunction({
        data: bodyFormData,
        Route: route,
        showalert: true,
        state: getState,
        isMultipart: true,
        dispatch,
      });
      
      dispatch(setLoading(false));
      callback(response);
    } catch (err) {
      dispatch(setLoading(false));
      callback(false);
    }
  };

export const EditGalleryItems =
  ({ callback, id, nameFailed, dataFailed }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const formData = new FormData();
      formData.append("id", id);
      if (nameFailed === "file" || nameFailed === "file_path") {
        appendFilePathField(formData, dataFailed);
      } else {
        formData.append(nameFailed, dataFailed);
      }
      
      const response = await PostFunction({
        data: formData,
        Route: UrlApi.gallery_items.edit,
        showalert: true,
        state: getState,
        isMultipart: nameFailed === "file" || nameFailed === "file_path",
        dispatch,
      });
      
      dispatch(setLoading(false));
      callback(response);
    } catch (err) {
      dispatch(setLoading(false));
      callback(false);
    }
  };

export const DeleteGalleryItems =
  ({ callback, id }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const response = await PostFunction({
        data: { ids: id },
        Route: UrlApi.gallery_items.delete,
        showalert: true,
        state: getState,
        isMultipart: false,
        dispatch: dispatch,
      });
      
      dispatch(setLoading(false));
      callback(response);
    } catch (err) {
      dispatch(setLoading(false));
      callback(false);
    }
  };

